import {
    AlignJustify,
    BarChart3,
    Boxes,
    Check,
    Grid3X3,
    ImageIcon,
    Info,
    Layers,
    LayoutGrid,
    List,
    Package,
    PanelLeft,
    Pencil,
    Plus,
    Scissors,
    Search,
    Settings,
    SlidersHorizontal,
    Tag,
    Trash2,
    X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    createCategory,
    createGroup,
    deleteCategory,
    deleteGroup,
    getCategoriesByType,
    getServices,
    searchServices,
    updateCategory,
} from '../../../api/service.api';
import {
    deletePackage,
    getPackageCategories,
    getPackages,
} from '../../../api/package.api';
import styles from '../../../assets/scss/pages/ServiceList.module.scss';
import { PERMISSIONS } from '../../../config/permissions';
import { useAdminHeaderSlot } from '../../../contexts/AdminHeaderContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { useUIStore } from '../../../stores/uiStore';
import useServiceTabsStore, { selectVisibleTabKeys } from '../../../stores/serviceTabsStore';
import { formatCurrency } from '../../../utils/formatters';

// Tab 'all' luôn đứng đầu; các tab type còn lại lấy thứ tự/ẩn-hiện từ serviceTabsStore.
const ALL_TAB = { value: 'all', label: 'Tất cả', icon: <Grid3X3 size={16} /> };
const TAB_META = {
    dichvu: { label: 'Dịch vụ', icon: <Scissors size={16} /> },
    sanpham: { label: 'Sản phẩm', icon: <Package size={16} /> },
    combo: { label: 'Combo', icon: <Layers size={16} /> },
    lieutrinh: { label: 'Gói dịch vụ', icon: <Boxes size={16} /> },
};
// Web hỗ trợ 4 tab type (gồm 'lieutrinh'/Gói dịch vụ — port logic từ app ServiceAdmin).
const SUPPORTED_TAB_KEYS = ['dichvu', 'sanpham', 'combo', 'lieutrinh'];

function removeDiacritics(str) {
    return str
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
}

function getPriceDisplay(service) {
    const variants = service.variants || [];
    if (variants.length === 0) return formatCurrency(service.price || 0);

    let maxPrice = 0;
    let minPrice = Infinity;
    variants.forEach((v) => {
        const price = v.salePrice && Number(v.salePrice) > 1000 ? Number(v.salePrice) : Number(v.retailPrice) || 0;
        if (price > maxPrice) maxPrice = price;
        if (price < minPrice) minPrice = price;
    });

    if (minPrice === maxPrice) return formatCurrency(minPrice);
    return `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`;
}

function hasSale(service) {
    return (service.variants || []).some((v) => (v.salePrice || 0) > 1000);
}

// Ô tìm kiếm trên header: giữ state nội bộ nên KHÔNG bị tạo lại element qua setSlot
// mỗi lần gõ phím — tránh ngắt phiên composition của bộ gõ tiếng Việt (mất/nhảy chữ).
// Mỗi lần đổi tab, component được remount qua key → tự reset về rỗng.
function HeaderSearchInput({ onSearch }) {
    const [value, setValue] = useState('');
    const handleChange = (e) => {
        const v = e.target.value;
        setValue(v);
        onSearch(v);
    };
    return (
        <div className={styles.hSearchWrap}>
            <Search size={14} />
            <input
                className={styles.hSearchInput}
                placeholder="Tìm kiếm..."
                value={value}
                onChange={handleChange}
            />
        </div>
    );
}

export default function ServiceList() {
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();
    const canCreate = hasPermission(PERMISSIONS.SERVICE.CREATE);
    // Gói dịch vụ tái sử dụng quyền sản phẩm/dịch vụ (SERVICE_*) — giống app.
    const canViewDetail = hasPermission(PERMISSIONS.SERVICE.VIEW_DETAIL);
    const canDeletePackage = hasPermission(PERMISSIONS.SERVICE.DELETE);
    const { setSlot } = useAdminHeaderSlot();
    const serviceLayout = useUIStore((s) => s.serviceLayout);
    const setServiceLayout = useUIStore((s) => s.setServiceLayout);
    const categoryLayout = useUIStore((s) => s.categoryLayout);
    const setCategoryLayout = useUIStore((s) => s.setCategoryLayout);

    const serviceTabs = useServiceTabsStore((s) => s.tabs);
    const TABS = useMemo(() => {
        const ordered = selectVisibleTabKeys(serviceTabs, SUPPORTED_TAB_KEYS).map((key) => ({
            value: key,
            label: TAB_META[key].label,
            icon: TAB_META[key].icon,
        }));
        return [ALL_TAB, ...ordered];
    }, [serviceTabs]);

    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'all');
    const isPackageTab = activeTab === 'lieutrinh';
    const [services, setServices] = useState([]);

    // ===== Gói dịch vụ (tab 'lieutrinh') — state riêng giống app =====
    const [packages, setPackages] = useState([]);
    const [packagesLoading, setPackagesLoading] = useState(false);
    const [packageCategories, setPackageCategories] = useState([]);
    const [pkgToDelete, setPkgToDelete] = useState(null);
    const [pkgDeleting, setPkgDeleting] = useState(false);
    const [categories, setCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const gridRef = useRef(null);

    // Server-side pagination
    const PAGE_SIZE = 30;
    const [svcPage, setSvcPage] = useState(1);
    const [svcHasMore, setSvcHasMore] = useState(true);
    const [svcTotal, setSvcTotal] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const observerRef = useRef(null);

    // Category management states
    const [categoryView, setCategoryView] = useState(false); // toggle product list vs category list
    const [categorySearchText, setCategorySearchText] = useState('');
    const [showCreateCategory, setShowCreateCategory] = useState(false);
    const [showEditCategory, setShowEditCategory] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [showGroupModal, setShowGroupModal] = useState(null);
    const [categoryForm, setCategoryForm] = useState({ name: '', code: '', description: '' });
    const [categorySaving, setCategorySaving] = useState(false);
    const [selectedGroupServices, setSelectedGroupServices] = useState(new Set());
    const [groupSearchText, setGroupSearchText] = useState('');
    const [groupModalServices, setGroupModalServices] = useState([]);
    const [groupModalLoading, setGroupModalLoading] = useState(false);
    const [groupModalLoadingMore, setGroupModalLoadingMore] = useState(false);
    const [groupModalPage, setGroupModalPage] = useState(1);
    const [groupModalHasMore, setGroupModalHasMore] = useState(true);
    const groupObserverRef = useRef(null);
    const [showSuccess, setShowSuccess] = useState('');
    const [infoMsg, setInfoMsg] = useState(''); // thông báo dạng alert (vd: thiếu quyền)
    const [layoutDropdown, setLayoutDropdown] = useState(null); // null | 'category' | 'service'
    const [showKpi, setShowKpi] = useState(false);
    const [imgErrorIds, setImgErrorIds] = useState(new Set());

    const handleImgError = (id) =>
        setImgErrorIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));

    // === Fetch services page (server-side pagination) ===
    const fetchServicesPage = useCallback(async (page, tab, catId, reset = false) => {
        if (page > 1) setLoadingMore(true);
        try {
            const params = { limit: PAGE_SIZE, page };
            if (tab !== 'all') params.typeService = tab;
            if (catId && catId !== 'all') params.categoryId = catId;
            const { data } = await getServices(params);
            const list = (data.services || []).filter((s) => !s.isDeleted);
            const pagination = data.pagination || {};
            setServices((prev) => {
                if (reset) return list;
                const existingIds = new Set(prev.map((s) => s.id));
                return [...prev, ...list.filter((s) => !existingIds.has(s.id))];
            });
            setSvcHasMore(page < (pagination.totalPages || 1));
            setSvcTotal(pagination.total || 0);
            setSvcPage(page);
        } catch (err) {
            console.error('Fetch services error:', err);
        } finally {
            setLoadingMore(false);
        }
    }, []);

    // === Fetch a page of services for the group modal (independent of the page list) ===
    const fetchGroupModalPage = useCallback(async (cat, page, reset = false) => {
        if (page > 1) setGroupModalLoadingMore(true);
        else setGroupModalLoading(true);
        try {
            const type = cat._type || (activeTab !== 'all' ? activeTab : undefined);
            const params = { limit: PAGE_SIZE, page };
            if (type) params.typeService = type;
            const { data } = await getServices(params);
            const list = (data.services || []).filter((s) => !s.isDeleted);
            const pagination = data.pagination || {};
            setGroupModalServices((prev) => {
                if (reset) return list;
                const existingIds = new Set(prev.map((s) => s.id));
                return [...prev, ...list.filter((s) => !existingIds.has(s.id))];
            });
            setGroupModalHasMore(page < (pagination.totalPages || 1));
            setGroupModalPage(page);
        } catch (err) {
            console.error('Fetch group modal services error:', err);
            if (reset) setGroupModalServices([]);
            setGroupModalHasMore(false);
        } finally {
            setGroupModalLoading(false);
            setGroupModalLoadingMore(false);
        }
    }, [activeTab]);

    // === Fetch categories (once on init + tab change) ===
    const fetchCategories = useCallback(async (tab) => {
        try {
            if (tab === 'all') {
                const [dvCats, spCats, comboCats] = await Promise.all([
                    getCategoriesByType('dichvu'),
                    getCategoriesByType('sanpham'),
                    getCategoriesByType('combo'),
                ]);
                const merge = (res, type) =>
                    (res.data.serviceCategory || res.data || []).map((c) => ({ ...c, _type: type }));
                const seen = new Set();
                const unique = [
                    ...merge(dvCats, 'dichvu'),
                    ...merge(spCats, 'sanpham'),
                    ...merge(comboCats, 'combo'),
                ].filter((cat) => !seen.has(cat.id) && seen.add(cat.id));
                setCategories(unique);
            } else {
                const { data } = await getCategoriesByType(tab);
                const list = data.serviceCategory || data || [];
                setCategories(list.map((c) => ({ ...c, _type: tab })));
            }
        } catch {
            setCategories([]);
        }
    }, []);

    // === Fetch gói dịch vụ (tab 'lieutrinh') — load toàn bộ rồi lọc client-side giống app ===
    const fetchPackages = useCallback(async () => {
        setPackagesLoading(true);
        try {
            const res = await getPackages();
            const list = res?.data?.data || res?.data || [];
            setPackages(Array.isArray(list) ? list : []);
        } catch {
            setPackages([]);
        } finally {
            setPackagesLoading(false);
        }
    }, []);

    const fetchPackageCategories = useCallback(async () => {
        try {
            const res = await getPackageCategories(true);
            setPackageCategories(res?.data?.data || []);
        } catch {
            setPackageCategories([]);
        }
    }, []);

    const handleDeletePackage = async () => {
        if (!pkgToDelete) return;
        setPkgDeleting(true);
        try {
            await deletePackage(pkgToDelete.id);
            showSuccessMsg('Đã xoá gói dịch vụ!');
            setPkgToDelete(null);
            fetchPackages();
        } catch (err) {
            console.error('Error deleting package:', err);
        } finally {
            setPkgDeleting(false);
        }
    };

    // === Init ===
    useEffect(() => {
        const init = async () => {
            setLoading(true);
            if (activeTab === 'lieutrinh') {
                await Promise.all([fetchPackages(), fetchPackageCategories()]);
            } else {
                await Promise.all([fetchServicesPage(1, 'all', 'all', true), fetchCategories('all')]);
            }
            setLoading(false);
        };
        init();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // === Tab / Category change (debounced to avoid double-fire) ===
    const isFirstRender = useRef(true);
    const prevTabRef = useRef(activeTab);
    const prevCatRef = useRef(selectedCategory);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const tabChanged = prevTabRef.current !== activeTab;
        prevTabRef.current = activeTab;
        prevCatRef.current = selectedCategory;
        if (activeTab === 'lieutrinh') {
            // Gói: load 1 lần toàn bộ, lọc client-side. Category chỉ refetch khi vừa đổi sang tab gói.
            fetchPackages();
            if (tabChanged) fetchPackageCategories();
            return;
        }
        setSvcPage(1);
        setSvcHasMore(true);
        fetchServicesPage(1, activeTab, selectedCategory, true);
        if (tabChanged) fetchCategories(activeTab);
    }, [activeTab, selectedCategory, fetchServicesPage, fetchCategories, fetchPackages, fetchPackageCategories]);

    // === Load more (sentinel) ===
    const loadMore = useCallback(() => {
        if (loadingMore || !svcHasMore) return;
        fetchServicesPage(svcPage + 1, activeTab, selectedCategory, false);
    }, [loadingMore, svcHasMore, svcPage, activeTab, selectedCategory, fetchServicesPage]);

    const sentinelRef = useCallback(
        (node) => {
            if (observerRef.current) observerRef.current.disconnect();
            if (!node) return;
            observerRef.current = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) loadMore();
                },
                { threshold: 0.1 },
            );
            observerRef.current.observe(node);
        },
        [loadMore],
    );

    // === Search (API) — chỉ áp cho dịch vụ; gói lọc client-side ===
    useEffect(() => {
        if (isPackageTab) {
            setSearchResults(null);
            return;
        }
        const q = searchText.trim();
        if (!q) {
            setSearchResults(null);
            return;
        }
        const timer = setTimeout(async () => {
            try {
                const { data } = await searchServices(q);
                setSearchResults(data.services || []);
            } catch {
                setSearchResults([]);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [searchText, isPackageTab]);

    const isSearching = searchResults !== null;
    const displayServices = isSearching ? searchResults : services;
    const totalServices = isSearching ? searchResults.length : svcTotal;
    const saleCount = services.filter(hasSale).length;

    // ===== Lọc gói client-side: bỏ gói đã xoá, lọc theo danh mục + tìm kiếm (tên/mã/giá) =====
    const filteredPackages = useMemo(() => {
        const search = removeDiacritics(searchText.trim());
        return packages.filter((pkg) => {
            if (pkg.isDeleted) return false;
            if (selectedCategory !== 'all' && pkg.categoryId !== selectedCategory) return false;
            if (!search) return true;
            const name = removeDiacritics(pkg.name || '');
            const code = removeDiacritics(pkg.code || '');
            const price = String(pkg.price || '');
            return name.includes(search) || code.includes(search) || price.includes(searchText.trim());
        });
    }, [packages, selectedCategory, searchText]);
    const packageTotalCount = packages.filter((p) => !p.isDeleted).length;

    const scrollToGrid = () => {
        gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setSelectedCategory('all');
        setSearchText('');
        setSearchResults(null);
    };

    const handleCategoryChange = (catId) => {
        setSelectedCategory(catId);
        scrollToGrid();
    };

    // ===== Category CRUD handlers =====
    const showSuccessMsg = (msg) => {
        setShowSuccess(msg);
        setTimeout(() => setShowSuccess(''), 1500);
    };

    const showInfoMsg = (msg) => {
        setInfoMsg(msg);
        setTimeout(() => setInfoMsg(''), 2000);
    };

    const handleCreateCategory = async () => {
        if (!categoryForm.name.trim()) return;
        setCategorySaving(true);
        try {
            await createCategory(categoryForm);
            showSuccessMsg('Tạo danh mục thành công');
            setCategoryForm({ name: '', code: '', description: '' });
            setShowCreateCategory(false);
            fetchServicesPage(1, activeTab, selectedCategory, true);
            fetchCategories(activeTab);
        } catch (err) {
            console.error('Error creating category:', err);
        } finally {
            setCategorySaving(false);
        }
    };

    const toggleCategoryView = () => {
        setCategoryView((prev) => !prev);
        setCategorySearchText('');
    };

    const filteredCategories = categories.filter((cat) => {
        if (!categorySearchText.trim()) return true;
        const keyword = removeDiacritics(categorySearchText.trim());
        const name = removeDiacritics(cat.name || '');
        const code = (cat.code || '').toLowerCase();
        return name.includes(keyword) || code.includes(keyword);
    });

    const handleUpdateCategory = async () => {
        if (!categoryForm.name.trim() || !showEditCategory) return;
        setCategorySaving(true);
        try {
            await updateCategory(showEditCategory.id, categoryForm);
            showSuccessMsg('Cập nhật danh mục thành công');
            setShowEditCategory(null);
            fetchServicesPage(1, activeTab, selectedCategory, true);
            fetchCategories(activeTab);
        } catch (err) {
            console.error('Error updating category:', err);
        } finally {
            setCategorySaving(false);
        }
    };

    const handleDeleteCategory = async () => {
        if (!showDeleteConfirm) return;
        setCategorySaving(true);
        try {
            await deleteCategory(showDeleteConfirm.id);
            showSuccessMsg('Xoá danh mục thành công');
            setShowDeleteConfirm(null);
            const newCat = selectedCategory === showDeleteConfirm.id ? 'all' : selectedCategory;
            if (selectedCategory === showDeleteConfirm.id) setSelectedCategory('all');
            fetchServicesPage(1, activeTab, newCat, true);
            fetchCategories(activeTab);
        } catch (err) {
            console.error('Error deleting category:', err);
        } finally {
            setCategorySaving(false);
        }
    };

    const openEditCategory = (cat) => {
        setCategoryForm({ name: cat.name || '', code: cat.code || '', description: cat.description || '' });
        setShowEditCategory(cat);
        setCategoryView(false);
    };

    const openGroupModal = (cat, mode) => {
        setShowGroupModal({ category: cat, mode });
        setSelectedGroupServices(new Set());
        setGroupSearchText('');
        setCategoryView(false);
        setGroupModalServices([]);
        setGroupModalPage(1);
        setGroupModalHasMore(true);
    };

    // === Group modal data: paginate when idle, server-search when typing ===
    useEffect(() => {
        if (!showGroupModal) return;
        const cat = showGroupModal.category;
        const q = groupSearchText.trim();
        if (!q) {
            setGroupModalPage(1);
            setGroupModalHasMore(true);
            fetchGroupModalPage(cat, 1, true);
            return;
        }
        const timer = setTimeout(async () => {
            setGroupModalLoading(true);
            try {
                const type = cat._type || (activeTab !== 'all' ? activeTab : undefined);
                const { data } = await searchServices(q);
                let list = (data.services || []).filter((s) => !s.isDeleted);
                if (type) list = list.filter((s) => s.typeService === type);
                setGroupModalServices(list);
                setGroupModalHasMore(false);
            } catch {
                setGroupModalServices([]);
                setGroupModalHasMore(false);
            } finally {
                setGroupModalLoading(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [showGroupModal, groupSearchText, activeTab, fetchGroupModalPage]);

    const loadMoreGroup = useCallback(() => {
        if (groupModalLoading || groupModalLoadingMore || !groupModalHasMore) return;
        if (!showGroupModal || groupSearchText.trim()) return;
        fetchGroupModalPage(showGroupModal.category, groupModalPage + 1, false);
    }, [
        groupModalLoading,
        groupModalLoadingMore,
        groupModalHasMore,
        showGroupModal,
        groupSearchText,
        groupModalPage,
        fetchGroupModalPage,
    ]);

    const groupSentinelRef = useCallback(
        (node) => {
            if (groupObserverRef.current) groupObserverRef.current.disconnect();
            if (!node) return;
            groupObserverRef.current = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) loadMoreGroup();
                },
                { threshold: 0.1 },
            );
            groupObserverRef.current.observe(node);
        },
        [loadMoreGroup],
    );

    const handleGroupSubmit = async () => {
        if (!showGroupModal || selectedGroupServices.size === 0) return;
        setCategorySaving(true);
        try {
            const { category, mode } = showGroupModal;
            const promises = Array.from(selectedGroupServices).map((serviceId) =>
                mode === 'add'
                    ? createGroup({ serviceId, categoryId: category.id })
                    : deleteGroup(serviceId, category.id),
            );
            await Promise.all(promises);
            showSuccessMsg(
                mode === 'add'
                    ? `Đã thêm ${selectedGroupServices.size} mục vào danh mục`
                    : `Đã xoá ${selectedGroupServices.size} mục khỏi danh mục`,
            );
            setShowGroupModal(null);
            fetchServicesPage(1, activeTab, selectedCategory, true);
            fetchCategories(activeTab);
        } catch (err) {
            console.error('Error updating group:', err);
        } finally {
            setCategorySaving(false);
        }
    };

    const toggleGroupService = (id) => {
        setSelectedGroupServices((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Group modal filtered services (search + pagination handled server-side on fetch)
    const groupFilteredServices = showGroupModal
        ? groupModalServices.filter((s) => {
              const type = showGroupModal.category._type;
              if (type && s.typeService !== type) return false;
              const existingIds = new Set((showGroupModal.category.groupLinks || []).map((g) => g.serviceId));
              return showGroupModal.mode === 'add' ? !existingIds.has(s.id) : existingIds.has(s.id);
          })
        : [];

    const tabLabel =
        activeTab === 'dichvu'
            ? 'dịch vụ'
            : activeTab === 'sanpham'
              ? 'sản phẩm'
              : activeTab === 'combo'
                ? 'combo'
                : 'mục';

    // === Header slot ===
    const CAT_LAYOUT_OPTS = [
        { key: 'chips', icon: <AlignJustify size={15} />, label: 'Danh mục chip' },
        { key: 'sidebar', icon: <PanelLeft size={15} />, label: 'Danh mục sidebar' },
    ];
    const SVC_LAYOUT_OPTS = [
        { key: 'grid', icon: <LayoutGrid size={15} />, label: 'Lưới lớn' },
        { key: 'compact', icon: <Grid3X3 size={15} />, label: 'Lưới nhỏ' },
        { key: 'list', icon: <List size={15} />, label: 'Danh sách' },
    ];
    const currentCatIcon = CAT_LAYOUT_OPTS.find((o) => o.key === categoryLayout)?.icon || CAT_LAYOUT_OPTS[0].icon;
    const currentSvcIcon = SVC_LAYOUT_OPTS.find((o) => o.key === serviceLayout)?.icon || SVC_LAYOUT_OPTS[0].icon;

    useEffect(() => {
        setSlot(
            <div className={styles.headerSlot}>
                {TABS.map((tab) => (
                    <button
                        key={tab.value}
                        className={`${styles.hTab}${activeTab === tab.value ? ` ${styles.hTabActive}` : ''}`}
                        onClick={() => handleTabChange(tab.value)}
                    >
                        {tab.label}
                    </button>
                ))}
                <div className={styles.hDivider} />
                <button
                    className={styles.hIconBtn}
                    onClick={() => navigate('/admin/settings/service-tabs')}
                    title="Cấu hình danh mục (thứ tự hiển thị)"
                >
                    <SlidersHorizontal size={15} />
                </button>
                <HeaderSearchInput key={activeTab} onSearch={setSearchText} />
                <div className={styles.hDivider} />
                {/* Category layout dropdown */}
                <div className={styles.hDropdownWrap}>
                    <button
                        className={styles.hIconBtn}
                        onClick={() => setLayoutDropdown((p) => (p === 'category' ? null : 'category'))}
                        title="Bố cục danh mục"
                    >
                        {currentCatIcon}
                    </button>
                    {layoutDropdown === 'category' && (
                        <div className={styles.hDropdown}>
                            {CAT_LAYOUT_OPTS.map((o) => (
                                <button
                                    key={o.key}
                                    className={`${styles.hDropdownItem}${categoryLayout === o.key ? ` ${styles.hDropdownItemActive}` : ''}`}
                                    onClick={() => {
                                        setCategoryLayout(o.key);
                                        setLayoutDropdown(null);
                                    }}
                                >
                                    {o.icon} {o.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                {/* Service layout dropdown */}
                <div className={styles.hDropdownWrap}>
                    <button
                        className={styles.hIconBtn}
                        onClick={() => setLayoutDropdown((p) => (p === 'service' ? null : 'service'))}
                        title="Bố cục hiển thị"
                    >
                        {currentSvcIcon}
                    </button>
                    {layoutDropdown === 'service' && (
                        <div className={styles.hDropdown}>
                            {SVC_LAYOUT_OPTS.map((o) => (
                                <button
                                    key={o.key}
                                    className={`${styles.hDropdownItem}${serviceLayout === o.key ? ` ${styles.hDropdownItemActive}` : ''}`}
                                    onClick={() => {
                                        setServiceLayout(o.key);
                                        setLayoutDropdown(null);
                                    }}
                                >
                                    {o.icon} {o.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className={styles.hDivider} />
                <button className={styles.hBtnPrimary} onClick={() => setShowKpi((p) => !p)}>
                    <BarChart3 size={15} /> Số liệu
                </button>
                {canCreate && (
                    <button
                        className={styles.hBtnPrimary}
                        onClick={() => navigate('/admin/services/create')}
                    >
                        <Plus size={15} /> Thêm mới
                    </button>
                )}
            </div>,
        );
        return () => setSlot(null);
    }, [activeTab, isPackageTab, canCreate, categoryLayout, serviceLayout, layoutDropdown, showKpi, setSlot, navigate, TABS]);

    return (
        <div className={styles.servicePage}>
            {/* ===== Tab Gói dịch vụ (lieutrinh) — port logic từ app ServiceAdmin ===== */}
            {isPackageTab && (
                <>
                    {/* Category filter (gói) */}
                    <div className={styles.filters}>
                        <div className={styles.categoryChipsRow}>
                            <div className={styles.categoryChips}>
                                <button
                                    className={`${styles.chip}${selectedCategory === 'all' ? ` ${styles.chipActive}` : ''}`}
                                    onClick={() => handleCategoryChange('all')}
                                >
                                    Tất cả ({packageTotalCount})
                                </button>
                                {packageCategories.map((cat) => {
                                    if (!cat || !cat.id) return null;
                                    return (
                                        <button
                                            key={cat.id}
                                            className={`${styles.chip}${selectedCategory === cat.id ? ` ${styles.chipActive}` : ''}`}
                                            onClick={() => handleCategoryChange(cat.id)}
                                        >
                                            {cat.name || 'Không có tên'} ({cat.packageCount || 0})
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className={styles.packageHint}>
                            <Info size={15} />
                            <span>
                                Danh mục chỉ hiện khi đã có ít nhất 1 gói. Để thêm gói vào danh mục, mở và chọn danh
                                mục trong phần thông tin gói.
                            </span>
                        </div>
                    </div>

                    {/* Grid gói */}
                    <div ref={gridRef} className={styles.gridWrapper}>
                        {packagesLoading ? (
                            <div className={styles.loading}>
                                <div className={styles.spinner} />
                                Đang tải gói dịch vụ...
                            </div>
                        ) : packages.length === 0 ? (
                            <div className={styles.empty}>Chưa có gói dịch vụ nào</div>
                        ) : (
                            <div
                                className={`${styles.grid} ${styles[`grid${serviceLayout.charAt(0).toUpperCase() + serviceLayout.slice(1)}`]}`}
                            >
                                {filteredPackages.map((pkg) => (
                                    <div
                                        key={pkg.id}
                                        className={`${styles.card} ${styles[`card${serviceLayout.charAt(0).toUpperCase() + serviceLayout.slice(1)}`]}`}
                                        onClick={() => {
                                            if (!canViewDetail) {
                                                showInfoMsg('Bạn không có quyền xem chi tiết gói dịch vụ');
                                                return;
                                            }
                                            navigate(`/admin/packages/${pkg.id}/edit`);
                                        }}
                                    >
                                        <div className={styles.cardImgWrap}>
                                            {pkg.urlImg && !imgErrorIds.has(pkg.id) ? (
                                                <img
                                                    src={pkg.urlImg}
                                                    alt={pkg.name}
                                                    className={styles.cardImg}
                                                    onError={() => handleImgError(pkg.id)}
                                                />
                                            ) : (
                                                <div className={styles.cardImgPlaceholder}>
                                                    <ImageIcon size={36} />
                                                </div>
                                            )}
                                            {canDeletePackage && (
                                                <button
                                                    className={styles.cardDelete}
                                                    title="Xoá gói"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setPkgToDelete(pkg);
                                                    }}
                                                >
                                                    <Trash2 size={15} />
                                                </button>
                                            )}
                                        </div>
                                        <div className={styles.cardBody}>
                                            <span className={styles.cardName}>{pkg.name}</span>
                                            <span className={styles.cardPriceRow}>
                                                {pkg.salePrice ? (
                                                    <>
                                                        <span className={styles.cardPriceOld}>
                                                            {formatCurrency(pkg.price)}
                                                        </span>
                                                        <span className={styles.cardPrice}>
                                                            {formatCurrency(pkg.salePrice)}
                                                        </span>
                                                    </>
                                                ) : (
                                                    <span className={styles.cardPrice}>
                                                        {formatCurrency(pkg.price || 0)}
                                                    </span>
                                                )}
                                            </span>
                                            <span className={styles.cardMetaRow}>
                                                {pkg.code && <span className={styles.cardCode}>{pkg.code}</span>}
                                                <span
                                                    className={`${styles.pkgStatus} ${pkg.isActive ? styles.pkgStatusOn : styles.pkgStatusOff}`}
                                                >
                                                    {pkg.isActive ? 'Đang bán' : 'Tạm tắt'}
                                                </span>
                                            </span>
                                            <span className={styles.cardCode}>
                                                {pkg.totalSessions} buổi
                                                {pkg.validityMonths ? ` · HSD ${pkg.validityMonths} tháng` : ''}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* ===== Nội dung dịch vụ / sản phẩm / combo ===== */}
            {!isPackageTab && (
            <>
            {/* KPI */}
            {showKpi && (
                <div className={styles.kpis}>
                    <div className={styles.kpi}>
                        <div className={styles.kpiContent}>
                            <span className={styles.kpiLabel}>TỔNG SỐ</span>
                            <span className={styles.kpiValue}>{totalServices}</span>
                        </div>
                        <div className={`${styles.kpiIcon} ${styles.kpiIconBlue}`}>
                            {activeTab === 'dichvu' ? (
                                <Scissors />
                            ) : activeTab === 'sanpham' ? (
                                <Package />
                            ) : activeTab === 'combo' ? (
                                <Layers />
                            ) : (
                                <Grid3X3 />
                            )}
                        </div>
                    </div>
                    <div className={styles.kpi}>
                        <div className={styles.kpiContent}>
                            <span className={styles.kpiLabel}>ĐANG GIẢM GIÁ</span>
                            <span className={styles.kpiValue}>{saleCount}</span>
                        </div>
                        <div className={`${styles.kpiIcon} ${styles.kpiIconRed}`}>
                            <Tag />
                        </div>
                    </div>
                    <div className={styles.kpi}>
                        <div className={styles.kpiContent}>
                            <span className={styles.kpiLabel}>DANH MỤC</span>
                            <span className={styles.kpiValue}>{categories.length}</span>
                        </div>
                        <div className={`${styles.kpiIcon} ${styles.kpiIconGreen}`}>
                            <Layers />
                        </div>
                    </div>
                </div>
            )}

            {/* Category filter */}
            <div className={styles.filters}>
                {categoryLayout === 'chips' && !categoryView && (
                    <div className={styles.categoryChipsRow}>
                        <div className={styles.categoryChips}>
                            <button
                                className={`${styles.chip}${selectedCategory === 'all' ? ` ${styles.chipActive}` : ''}`}
                                onClick={() => handleCategoryChange('all')}
                            >
                                Tất cả
                            </button>
                            {categories.map((cat) => (
                                <button
                                    key={cat.id}
                                    className={`${styles.chip}${selectedCategory === cat.id ? ` ${styles.chipActive}` : ''}`}
                                    onClick={() => handleCategoryChange(cat.id)}
                                >
                                    {cat.name} {selectedCategory === cat.id && `(${svcTotal})`}
                                </button>
                            ))}
                        </div>
                        <button
                            className={styles.categoryGearBtn}
                            onClick={toggleCategoryView}
                            title="Quản lý danh mục"
                        >
                            <Settings size={18} />
                        </button>
                    </div>
                )}
            </div>

            {/* Quick action for selected category */}
            {!categoryView &&
                selectedCategory !== 'all' &&
                (() => {
                    const selectedCat = categories.find((c) => c.id === selectedCategory);
                    if (!selectedCat) return null;
                    return (
                        <div className={styles.categoryQuickActions}>
                            <button
                                className={styles.categoryQuickBtn}
                                onClick={() => openGroupModal(selectedCat, 'add')}
                            >
                                <Plus size={16} />
                                Thêm {tabLabel} vào danh mục "{selectedCat.name}"
                            </button>
                            <button
                                className={styles.categoryQuickBtn}
                                onClick={() => openGroupModal(selectedCat, 'remove')}
                            >
                                <Scissors size={16} />
                                Xoá {tabLabel} khỏi danh mục
                            </button>
                            <button className={styles.categoryQuickBtn} onClick={() => openEditCategory(selectedCat)}>
                                <Pencil size={16} />
                                Sửa danh mục
                            </button>
                        </div>
                    );
                })()}

            {categoryView ? (
                /* ===== Category Management View ===== */
                <div className={styles.categoryManageView}>
                    <div className={styles.categoryManageHeader}>
                        <div className={styles.categoryManageSearchWrap}>
                            <Search className={styles.searchIcon} />
                            <input
                                className={styles.searchInput}
                                placeholder="Tìm kiếm danh mục..."
                                value={categorySearchText}
                                onChange={(e) => setCategorySearchText(e.target.value)}
                            />
                        </div>
                        <div className={styles.categoryManageHeaderRight}>
                            <button className={styles.btnCancel} onClick={toggleCategoryView}>
                                Huỷ
                            </button>
                            <button
                                className={`${styles.btn} ${styles.btnPrimary}`}
                                onClick={() => {
                                    setCategoryForm({ name: '', code: '', description: '' });
                                    setShowCreateCategory(true);
                                }}
                            >
                                <Plus size={18} /> Tạo danh mục
                            </button>
                        </div>
                    </div>
                    <div className={styles.categoryManageList}>
                        {loading ? (
                            <div className={styles.loading}>
                                <div className={styles.spinner} />
                                Đang tải dữ liệu...
                            </div>
                        ) : filteredCategories.length === 0 ? (
                            <div className={styles.empty}>Không tìm thấy danh mục nào</div>
                        ) : (
                            filteredCategories.map((cat) => {
                                const count = (cat.groupLinks || []).length;
                                return (
                                    <div
                                        key={cat.id}
                                        className={styles.categoryManageItem}
                                        onClick={() => {
                                            setSelectedCategory(cat.id);
                                            setCategoryView(false);
                                        }}
                                    >
                                        <div className={styles.categoryManageInfo}>
                                            <span className={styles.categoryManageName}>{cat.name}</span>
                                            <span className={styles.categoryManageMeta}>
                                                {cat.code && <span>{cat.code}</span>}
                                                {cat.description && <span>{cat.description}</span>}
                                                <span>
                                                    {count} {tabLabel}
                                                </span>
                                            </span>
                                        </div>
                                        <button
                                            className={`${styles.categoryManageActionBtn} ${styles.categoryManageActionDanger}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setShowDeleteConfirm(cat);
                                            }}
                                            title="Xoá danh mục"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            ) : (
                /* ===== Product List View ===== */
                <>
                    {/* Content area */}
                    <div className={categoryLayout === 'sidebar' ? styles.contentWithSidebar : undefined}>
                        {/* Category sidebar */}
                        {categoryLayout === 'sidebar' && (
                            <div className={styles.categorySidebar}>
                                <div className={styles.sidebarHeader}>
                                    <div className={styles.sidebarTitle}>Danh mục</div>
                                    <button
                                        className={styles.categoryGearBtn}
                                        onClick={toggleCategoryView}
                                        title="Quản lý danh mục"
                                    >
                                        <Settings size={16} />
                                    </button>
                                </div>
                                <button
                                    className={`${styles.sidebarItem}${selectedCategory === 'all' ? ` ${styles.sidebarItemActive}` : ''}`}
                                    onClick={() => handleCategoryChange('all')}
                                >
                                    Tất cả
                                    <span className={styles.sidebarCount}>{svcTotal}</span>
                                </button>
                                {categories.map((cat) => {
                                    const count = (cat.groupLinks || []).length;
                                    return (
                                        <button
                                            key={cat.id}
                                            className={`${styles.sidebarItem}${selectedCategory === cat.id ? ` ${styles.sidebarItemActive}` : ''}`}
                                            onClick={() => handleCategoryChange(cat.id)}
                                        >
                                            {cat.name}
                                            <span className={styles.sidebarCount}>{count}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {/* Grid */}
                        <div ref={gridRef} className={styles.gridWrapper}>
                            {loading ? (
                                <div className={styles.loading}>
                                    <div className={styles.spinner} />
                                    Đang tải...
                                </div>
                            ) : displayServices.length === 0 ? (
                                <div className={styles.empty}>Không tìm thấy dịch vụ nào</div>
                            ) : (
                                <>
                                    <div
                                        className={`${styles.grid} ${styles[`grid${serviceLayout.charAt(0).toUpperCase() + serviceLayout.slice(1)}`]}`}
                                    >
                                        {displayServices.map((service) => (
                                            <div
                                                key={service.id}
                                                className={`${styles.card} ${styles[`card${serviceLayout.charAt(0).toUpperCase() + serviceLayout.slice(1)}`]}`}
                                                onClick={() => navigate(`/admin/services/${service.id}`)}
                                            >
                                                <div className={styles.cardImgWrap}>
                                                    {service.urlImg && !imgErrorIds.has(service.id) ? (
                                                        <img
                                                            src={service.urlImg}
                                                            alt={service.name}
                                                            className={styles.cardImg}
                                                            onError={() => handleImgError(service.id)}
                                                        />
                                                    ) : (
                                                        <div className={styles.cardImgPlaceholder}>
                                                            <ImageIcon size={36} />
                                                        </div>
                                                    )}
                                                    {hasSale(service) && <span className={styles.saleBadge}>Sale</span>}
                                                </div>
                                                <div className={styles.cardBody}>
                                                    <span className={styles.cardName}>{service.name}</span>
                                                    {service.code && (
                                                        <span className={styles.cardCode}>{service.code}</span>
                                                    )}
                                                    <span className={styles.cardPrice}>{getPriceDisplay(service)}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {!isSearching && svcHasMore && (
                                        <div ref={sentinelRef} className={styles.loadMoreHint}>
                                            Đang tải thêm...
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}
            </>
            )}

            {/* ===== Xác nhận xoá gói dịch vụ ===== */}
            {pkgToDelete && (
                <div className={styles.modalOverlay} onClick={() => setPkgToDelete(null)}>
                    <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Xoá gói dịch vụ</h3>
                            <button className={styles.modalClose} onClick={() => setPkgToDelete(null)}>
                                <X size={20} />
                            </button>
                        </div>
                        <p className={styles.confirmText}>
                            Bạn có chắc chắn muốn xoá gói dịch vụ <strong>{pkgToDelete.name}</strong>?
                        </p>
                        <div className={styles.modalFooter}>
                            <button className={styles.btnCancel} onClick={() => setPkgToDelete(null)}>
                                Huỷ
                            </button>
                            <button
                                className={`${styles.btn} ${styles.btnDanger}`}
                                onClick={handleDeletePackage}
                                disabled={pkgDeleting}
                            >
                                {pkgDeleting ? 'Đang xoá...' : 'Xoá'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Create Category Modal ===== */}
            {showCreateCategory && (
                <div className={styles.modalOverlay} onClick={() => setShowCreateCategory(false)}>
                    <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Tạo danh mục mới</h3>
                            <button className={styles.modalClose} onClick={() => setShowCreateCategory(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Tên danh mục <span className={styles.formRequired}>*</span>
                            </label>
                            <input
                                className={styles.formInput}
                                placeholder="VD: Nước hoa"
                                value={categoryForm.name}
                                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Mã danh mục</label>
                            <input
                                className={styles.formInput}
                                placeholder="VD: DV_NH"
                                value={categoryForm.code}
                                onChange={(e) => setCategoryForm({ ...categoryForm, code: e.target.value })}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Mô tả</label>
                            <input
                                className={styles.formInput}
                                placeholder="Mô tả danh mục..."
                                value={categoryForm.description}
                                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                            />
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.btnCancel} onClick={() => setShowCreateCategory(false)}>
                                Huỷ
                            </button>
                            <button
                                className={`${styles.btn} ${styles.btnPrimary}`}
                                onClick={handleCreateCategory}
                                disabled={categorySaving || !categoryForm.name.trim()}
                            >
                                {categorySaving ? 'Đang tạo...' : 'Tạo danh mục'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Edit Category Modal ===== */}
            {showEditCategory && (
                <div className={styles.modalOverlay} onClick={() => setShowEditCategory(null)}>
                    <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Sửa danh mục</h3>
                            <button className={styles.modalClose} onClick={() => setShowEditCategory(null)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Tên danh mục <span className={styles.formRequired}>*</span>
                            </label>
                            <input
                                className={styles.formInput}
                                value={categoryForm.name}
                                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Mã danh mục</label>
                            <input
                                className={styles.formInput}
                                value={categoryForm.code}
                                onChange={(e) => setCategoryForm({ ...categoryForm, code: e.target.value })}
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>Mô tả</label>
                            <input
                                className={styles.formInput}
                                value={categoryForm.description}
                                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                            />
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.btnCancel} onClick={() => setShowEditCategory(null)}>
                                Huỷ
                            </button>
                            <button
                                className={`${styles.btn} ${styles.btnPrimary}`}
                                onClick={handleUpdateCategory}
                                disabled={categorySaving || !categoryForm.name.trim()}
                            >
                                {categorySaving ? 'Đang lưu...' : 'Lưu thay đổi'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Delete Confirm Modal ===== */}
            {showDeleteConfirm && (
                <div className={styles.modalOverlay} onClick={() => setShowDeleteConfirm(null)}>
                    <div className={styles.modalBox} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>Xoá danh mục</h3>
                            <button className={styles.modalClose} onClick={() => setShowDeleteConfirm(null)}>
                                <X size={20} />
                            </button>
                        </div>
                        <p className={styles.confirmText}>
                            Bạn có chắc chắn muốn xoá danh mục <strong>{showDeleteConfirm.name}</strong>?
                        </p>
                        <div className={styles.modalFooter}>
                            <button className={styles.btnCancel} onClick={() => setShowDeleteConfirm(null)}>
                                Huỷ
                            </button>
                            <button
                                className={`${styles.btn} ${styles.btnDanger}`}
                                onClick={handleDeleteCategory}
                                disabled={categorySaving}
                            >
                                {categorySaving ? 'Đang xoá...' : 'Xoá'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Group (Add/Remove services) Modal ===== */}
            {showGroupModal && (
                <div className={styles.modalOverlay} onClick={() => setShowGroupModal(null)}>
                    <div className={`${styles.modalBox} ${styles.modalLarge}`} onClick={(e) => e.stopPropagation()}>
                        <div className={styles.modalHeader}>
                            <h3 className={styles.modalTitle}>
                                {showGroupModal.mode === 'add' ? 'Thêm' : 'Xoá'} {tabLabel} -{' '}
                                {showGroupModal.category.name}
                            </h3>
                            <button className={styles.modalClose} onClick={() => setShowGroupModal(null)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className={styles.groupSearch}>
                            <Search className={styles.searchIcon} />
                            <input
                                className={styles.searchInput}
                                placeholder="Tìm kiếm..."
                                value={groupSearchText}
                                onChange={(e) => setGroupSearchText(e.target.value)}
                            />
                        </div>
                        <div className={styles.groupSelectedCount}>
                            Đã chọn: {selectedGroupServices.size} {tabLabel}
                        </div>
                        <div className={styles.groupServiceList}>
                            {groupModalLoading ? (
                                <div className={styles.modalEmpty}>Đang tải...</div>
                            ) : groupFilteredServices.length === 0 &&
                              (groupSearchText.trim() || !groupModalHasMore) ? (
                                <div className={styles.modalEmpty}>
                                    {showGroupModal.mode === 'add'
                                        ? `Không còn ${tabLabel} nào để thêm`
                                        : `Danh mục chưa có ${tabLabel} nào`}
                                </div>
                            ) : (
                                <>
                                    {groupFilteredServices.map((s) => (
                                        <div
                                            key={s.id}
                                            className={`${styles.groupServiceItem}${selectedGroupServices.has(s.id) ? ` ${styles.groupServiceSelected}` : ''}`}
                                            onClick={() => toggleGroupService(s.id)}
                                        >
                                            {s.urlImg && !imgErrorIds.has(s.id) ? (
                                                <img
                                                    src={s.urlImg}
                                                    alt={s.name}
                                                    className={styles.groupServiceImg}
                                                    onError={() => handleImgError(s.id)}
                                                />
                                            ) : (
                                                <div className={styles.groupServiceImgPlaceholder}>
                                                    <ImageIcon size={20} />
                                                </div>
                                            )}
                                            <div className={styles.groupServiceInfo}>
                                                <span className={styles.groupServiceName}>{s.name}</span>
                                                {s.code && <span className={styles.groupServiceCode}>{s.code}</span>}
                                            </div>
                                            <div className={styles.groupServiceCheck}>
                                                {selectedGroupServices.has(s.id) && <Check size={20} />}
                                            </div>
                                        </div>
                                    ))}
                                    {!groupSearchText.trim() && groupModalHasMore && (
                                        <div ref={groupSentinelRef} className={styles.loadMoreHint}>
                                            Đang tải thêm...
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.btnCancel} onClick={() => setShowGroupModal(null)}>
                                Huỷ
                            </button>
                            <button
                                className={`${styles.btn} ${showGroupModal.mode === 'add' ? styles.btnPrimary : styles.btnDanger}`}
                                onClick={handleGroupSubmit}
                                disabled={categorySaving || selectedGroupServices.size === 0}
                            >
                                {categorySaving
                                    ? showGroupModal.mode === 'add'
                                        ? 'Đang thêm...'
                                        : 'Đang xoá...'
                                    : `${showGroupModal.mode === 'add' ? 'Thêm' : 'Xoá'} ${selectedGroupServices.size} ${tabLabel}`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ===== Success Toast ===== */}
            {showSuccess && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div className={styles.successIcon}>✓</div>
                        <span className={styles.successText}>{showSuccess}</span>
                    </div>
                </div>
            )}

            {/* ===== Thông báo (alert) — vd thiếu quyền ===== */}
            {infoMsg && (
                <div className={styles.successOverlay}>
                    <div className={styles.successModal}>
                        <div className={`${styles.successIcon} ${styles.infoIcon}`}>
                            <Info size={22} />
                        </div>
                        <span className={styles.successText}>{infoMsg}</span>
                    </div>
                </div>
            )}
        </div>
    );
}
